import { useAuthStore } from '@/store/authStore';
import { useDashboardData } from '@/hooks/useDashboardData';
import {
  DashboardHeader,
  StatsCards,
  RecentTransactions,
  MiniSankeyFlow,
  ConnectionStatus,
  QuickActions,
  RetirementPlanningCard,
} from '@/components/dashboard';
import Alert from '@/components/ui/Alert';
import { ApiClientError } from '@/services/api';
import { getUserFriendlyMessage } from '@/utils/errorMessages';

/**
 * Dashboard Page - Main landing page for authenticated users
 * Displays financial overview with real-time data from connected accounts
 */
export default function DashboardPage() {
  const { userName } = useAuthStore();
  const {
    isLoading,
    isError,
    error,
    stats,
    recentTransactions,
    connections,
    hasConnections,
    flowSnapshot,
    flowRateLimitMessage,
    refetch,
  } = useDashboardData();

  // Generate user-friendly error message
  const getErrorMessage = (): string => {
    if (error instanceof ApiClientError) {
      return getUserFriendlyMessage(error.category, error.requestId);
    }
    return 'Unable to load some dashboard data. Please try again.';
  };

  return (
    <div className="mx-auto max-w-7xl">
      {/* Welcome header */}
      <DashboardHeader userName={userName} />

      {/* Error state */}
      {isError && (
        <Alert variant="error" className="mb-6">
          <div className="flex items-center justify-between">
            <span>{getErrorMessage()}</span>
            <button
              onClick={refetch}
              className="ml-4 text-sm font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        </Alert>
      )}

      {/* Stats cards */}
      <StatsCards stats={stats} isLoading={isLoading} />

      {/* RetirementPlanningCard - full width (WP-RETIRE-2) */}
      <div className="mb-6">
        <RetirementPlanningCard />
      </div>

      {/* Main content grid - responsive layout */}
      {/* First row - Money flow (center stage) and connection status */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Left column - Mini Sankey flow (spans 2 cols) - CENTER STAGE */}
        {/* WP-DASH-2: Replaced MiniSankeyPreview with actual Sankey visualization */}
        <div className="md:col-span-2 lg:col-span-2">
          <MiniSankeyFlow
            flowSnapshot={flowSnapshot}
            isLoading={isLoading}
            hasConnections={hasConnections}
            rateLimitMessage={flowRateLimitMessage}
          />
        </div>

        {/* Right column - Connection status */}
        <ConnectionStatus connections={connections} isLoading={isLoading} />
      </div>

      {/* Second row - Recent transactions and quick actions */}
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Left column - Recent transactions (spans 2 cols on large screens) */}
        <RecentTransactions
          transactions={recentTransactions}
          isLoading={isLoading}
          hasConnections={hasConnections}
        />

        {/* Right column - Quick actions */}
        <QuickActions hasConnections={hasConnections} />
      </div>
    </div>
  );
}
